/**
 * The running walkthrough: a spotlight on a real control, and a panel beside it.
 *
 * New work with no precedent in this repo, so the rules it has to honour are worth
 * stating where they are implemented:
 *
 * - **The spotlight never blocks the control.** `pointer-events: none` on the ring, and
 *   no full-screen click-catcher. The user is operating the live tool through this, and
 *   an overlay that swallowed the click would make the walkthrough impossible to finish.
 * - **A satisfied step shows "Already complete" and offers Next or End.** It does not
 *   advance itself. `docs/gms/PHASES.md` §3 OD9 is explicit — "do not silently rush
 *   forward" —
 *   and rushing is exactly what a well-meaning auto-advance would do.
 * - **Exit warns when unsaved tool data may be lost**, and says plainly that saved work
 *   stays saved. Those are different things and conflating them would frighten people
 *   out of finishing.
 * - **It never traps the user.** End is always reachable, Escape works, and the panel is
 *   positioned away from the spotlight rather than over it.
 *
 * Two kinds of step (G19). A **tagged** step's control is found by its
 * `data-gms-target`, and the host component reports the action itself through `emit`.
 * A **recorded** step's element is found by its locator, and this overlay watches it for
 * the recorded action, reporting through the same `emit` and the same five-field
 * envelope with the step's own key as the target (G21). Neither kind ever guesses: an
 * element that cannot be found is said to be missing.
 *
 * Only the real action reports through `emit`. **Next** (a step that is already done,
 * optional or look-only) and **Skip** (a recorded step whose element is gone) are the
 * person choosing to move on, so they go through `onPass` with a reason the server
 * checks (G23) — they never claim the action happened.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { GMS_UI_ATTR, resolveLocator } from '../recorder/locator';
import { attachAdapter, detachAdapter, emit } from '../services/gms-adapter';
import type { GmsSignal, GuideScreen, PassReason, WalkthroughStepState } from '../types';

interface WalkthroughOverlayProps {
  toolKey: string;
  topicKey: string;
  title: string;
  steps: WalkthroughStepState[];
  currentStepKey: string;
  isPreview: boolean;
  /** Screens the user may see, to name the one a recorded step lives on. */
  screens: GuideScreen[];
  /** Whether the tool currently holds unsaved input; drives the exit warning. */
  hasUnsavedToolData?: boolean;
  /** Called with the key of the step to show next, once the server has advanced. */
  onAdvance: (nextStepKey: string) => void;
  onEnd: () => void;
  onComplete: (signal: string) => void;
  /**
   * Move past `stepKey` without its action (G23). Resolves once the server has moved
   * on; rejects with the server's reason when it refuses.
   */
  onPass: (stepKey: string, reason: PassReason) => Promise<void>;
}

/** How long a step's element may be absent before the panel says it is missing. */
const MISSING_AFTER_MS = 1500;
/** How often a missing or detached element is looked for again. */
const RESOLVE_EVERY_MS = 300;

const gmsUi = { [GMS_UI_ATTR]: '' };

/** Find a step's element: its tag, or for a recorded step its locator. */
function findElement(step: WalkthroughStepState): HTMLElement | null {
  if (step.locator) return resolveLocator(step.locator);
  return document.querySelector<HTMLElement>(`[data-gms-target="${step.target_key}"]`);
}

/** Track a step's element and its on-screen box, following scroll and resize. */
function useStepElement(step: WalkthroughStepState | null, onRoute: boolean) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setRect(null);
    setElement(null);
    setMissing(false);
    if (!step || !onRoute) return undefined;
    let frame = 0;
    let current: HTMLElement | null = null;
    let lastResolve = 0;
    const since = performance.now();
    const measure = (now: number) => {
      if (!current?.isConnected && now - lastResolve >= RESOLVE_EVERY_MS) {
        lastResolve = now;
        const found = findElement(step);
        if (found !== current) {
          current = found;
          setElement(found);
        }
      }
      setRect(current?.isConnected ? current.getBoundingClientRect() : null);
      setMissing(!current?.isConnected && now - since > MISSING_AFTER_MS);
      frame = window.requestAnimationFrame(measure);
    };
    frame = window.requestAnimationFrame(measure);
    return () => window.cancelAnimationFrame(frame);
  }, [step, onRoute]);

  // Bring a newly found element into view — a field low in a scrolling dialog would
  // otherwise be spotlit off-screen. Once per element, so the user can scroll away.
  useEffect(() => {
    if (!element) return;
    const box = element.getBoundingClientRect();
    if (box.top >= 0 && box.bottom <= window.innerHeight) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [element]);

  return { rect, element, missing };
}

/** The panel's footprint in its default bottom-right corner, with the CSS margin. */
const PANEL_MARGIN_PX = 24;
const PANEL_WIDTH_PX = 384;

/** Whether the spotlit box would sit under the panel in its bottom-right corner. */
function overlapsPanelCorner(rect: DOMRect, panelHeight: number): boolean {
  return (
    rect.right > window.innerWidth - PANEL_WIDTH_PX - PANEL_MARGIN_PX &&
    rect.bottom > window.innerHeight - panelHeight - PANEL_MARGIN_PX
  );
}

export function WalkthroughOverlay({
  toolKey,
  topicKey,
  title,
  steps,
  currentStepKey,
  isPreview,
  screens,
  hasUnsavedToolData = false,
  onAdvance,
  onEnd,
  onComplete,
  onPass,
}: WalkthroughOverlayProps) {
  const [confirmingExit, setConfirmingExit] = useState(false);
  const [refusal, setRefusal] = useState<string>('');
  const [passing, setPassing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // The step a signal has already been sent for, so one action reports once.
  const sentFor = useRef<string>('');
  const location = useLocation();

  const index = steps.findIndex((step) => step.step_key === currentStepKey);
  const step = index >= 0 ? steps[index] : null;
  const isFinalStep = index === steps.length - 1;
  const locator = step?.locator ?? null;
  const onRoute =
    !locator || Boolean(matchPath({ path: locator.route, end: true }, location.pathname));
  const { rect, element, missing } = useStepElement(step, onRoute);

  // The adapter is wired once per step. Callbacks and the step list are read through a
  // ref at call time, so the host re-rendering (inline callbacks, a refetched step
  // list) neither re-attaches it nor clears `sentFor` mid-step.
  const latest = useRef({ steps, index, isFinalStep, onAdvance, onComplete });
  latest.current = { steps, index, isFinalStep, onAdvance, onComplete };
  const stepKey = step?.step_key ?? '';
  const completionSignal = step?.completion_signal_key ?? '';

  // Register this walkthrough as the recipient of adapter signals while it runs, and
  // deregister on unmount so a stale overlay can never receive one.
  useEffect(() => {
    if (!stepKey) return undefined;
    sentFor.current = '';
    setRefusal('');
    attachAdapter({
      toolKey,
      topicKey,
      stepKey,
      onAccepted: () => {
        setRefusal('');
        // The server has moved on; show the step after this one.
        const now = latest.current;
        if (now.isFinalStep) now.onComplete(completionSignal);
        else now.onAdvance(now.steps[now.index + 1].step_key);
      },
      onRefused: (code, detail) => {
        // Refused or never delivered: this step may report again.
        sentFor.current = '';
        // An incompatible target means the screen and the manifest disagree. Say so;
        // never fall back to a guess at which control was meant.
        setRefusal(
          code === 'walkthrough_incompatible'
            ? 'This walkthrough no longer matches the screen, so it has stopped here.'
            : detail || 'That action was not the one this step is waiting for.'
        );
      },
    });
    return () => detachAdapter();
  }, [toolKey, topicKey, stepKey, completionSignal]);

  /**
   * The recorded action happened: report it. A recorded step names itself as the
   * target (G21). Once per step, until the server refuses or cannot be reached.
   */
  const reportStep = useCallback(() => {
    if (!step || sentFor.current === step.step_key) return;
    sentFor.current = step.step_key;
    emit(step.locator ? step.step_key : step.target_key, step.completion_signal_key as GmsSignal);
  }, [step]);

  /** Next or Skip: move on without the action; the server checks the reason (G23). */
  const passStep = useCallback(
    (reason: PassReason) => {
      if (!step || passing) return;
      setPassing(true);
      setRefusal('');
      onPass(step.step_key, reason)
        .catch((error: unknown) =>
          setRefusal(error instanceof Error ? error.message : 'This step could not be passed.')
        )
        .finally(() => setPassing(false));
    },
    [step, passing, onPass]
  );

  // A recorded step watches its element for the recorded action.
  useEffect(() => {
    if (!step?.locator || !element) return undefined;
    const action = step.locator.action;
    if (action === 'none') return undefined;
    const type = action === 'click' ? 'click' : 'change';
    element.addEventListener(type, reportStep, true);
    return () => element.removeEventListener(type, reportStep, true);
  }, [step, element, reportStep]);

  const requestExit = useCallback(() => {
    if (hasUnsavedToolData) setConfirmingExit(true);
    else onEnd();
  }, [hasUnsavedToolData, onEnd]);

  // Escape ends the walkthrough (through the same warning), so nobody is ever trapped.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestExit();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [requestExit]);

  if (!step) return null;

  const stepScreen = locator
    ? screens.find((screen) => screen.route_pattern === locator.route)
    : undefined;
  const stepScreenName = stepScreen?.display_name ?? locator?.route ?? '';
  const canLinkToScreen = Boolean(locator && !locator.route.includes(':'));
  // A step that only looks has nothing to watch for, so the person moves on.
  const looksOnly = locator?.action === 'none';
  // Why Next may be offered here, in the server's terms; null hides it.
  // Keep the panel off the control it explains: top corner when the bottom one covers it.
  const panelOnTop = Boolean(
    rect && overlapsPanelCorner(rect, panelRef.current?.offsetHeight ?? 0)
  );
  const nextReason: PassReason | null = step.already_complete
    ? 'already_complete'
    : step.optional_step
      ? 'optional'
      : looksOnly && onRoute
        ? 'look'
        : null;

  return createPortal(
    <>
      {rect ? (
        <div
          className="wb-gms-spotlight"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
          aria-hidden="true"
          {...gmsUi}
        />
      ) : null}

      <div
        ref={panelRef}
        className={`wb-gms-step-panel${panelOnTop ? ' wb-gms-step-panel--top' : ''}`}
        role="dialog"
        aria-label={`${title} — step ${index + 1} of ${steps.length}`}
        {...gmsUi}
      >
        <div className="wb-gms-step-panel__body">
          {isPreview ? (
            <div className="wb-gms-note wb-gms-note--warning">
              Preview. No XP and no completion are recorded — but the tool underneath is
              live, so anything you save is real.
            </div>
          ) : null}

          {step.already_complete ? (
            <div className="wb-gms-already-complete">
              <strong>Already complete.</strong>{' '}
              {step.already_complete_detail ||
                'This step is done. Carry on when you are ready.'}
            </div>
          ) : null}

          {!onRoute ? (
            <div className="wb-gms-note wb-gms-note--info" role="status">
              {canLinkToScreen && locator ? (
                <Link to={locator.route}>Go to {stepScreenName}</Link>
              ) : (
                <>Go to {stepScreenName}</>
              )}{' '}
              to continue with this step.
            </div>
          ) : null}

          {onRoute && missing ? (
            <div className="wb-gms-note wb-gms-note--caution" role="alert">
              {locator
                ? "This step can't be found on the screen — the page may have changed."
                : 'We cannot find the control this step points at on the current screen, so the walkthrough has stopped rather than guessing.'}
            </div>
          ) : null}

          {refusal ? (
            <div className="wb-gms-note wb-gms-note--caution" role="alert">
              {refusal}
            </div>
          ) : null}

          <p>{step.instruction}</p>

          {step.optional_step ? (
            <p className="wb-gms-entry__meta">
              Optional — add as much or as little as you like; it will not hold you up.
            </p>
          ) : null}
        </div>

        <div className="wb-gms-step-panel__footer">
          <span className="wb-gms-step-panel__progress">
            Step {index + 1} of {steps.length}
          </span>

          {/*
            Next appears when the step is already satisfied, optional, or only asks the
            person to look. It never appears on an unsatisfied required step: the point
            of the walkthrough is that the real action happens, not that the panel is
            clicked through. Skip is the honest exception for a recorded step whose
            element is gone — the person chooses to move on, nothing is guessed.
          */}
          {nextReason ? (
            <button
              type="button"
              className="wb-gms-entry"
              style={{ width: 'auto', marginBottom: 0 }}
              disabled={passing}
              onClick={() => passStep(nextReason)}
            >
              Next
            </button>
          ) : null}

          {locator && onRoute && missing && !step.optional_step ? (
            <button
              type="button"
              className="wb-gms-entry"
              style={{ width: 'auto', marginBottom: 0 }}
              disabled={passing}
              onClick={() => passStep('unavailable')}
            >
              Skip
            </button>
          ) : null}

          {step.allow_end ? (
            <button
              type="button"
              className="wb-gms-entry"
              style={{ width: 'auto', marginBottom: 0 }}
              onClick={requestExit}
            >
              End
            </button>
          ) : null}
        </div>
      </div>

      {confirmingExit ? (
        <div
          className="wb-gms-step-panel"
          role="alertdialog"
          aria-label="Leave walkthrough"
          {...gmsUi}
        >
          <div className="wb-gms-step-panel__body">
            <p>
              <strong>Leave this walkthrough?</strong>
            </p>
            <p>
              Anything you have already saved stays saved. Anything typed into the form
              but not yet saved will be lost.
            </p>
          </div>
          <div className="wb-gms-step-panel__footer">
            <button
              type="button"
              className="wb-gms-entry"
              style={{ width: 'auto', marginBottom: 0 }}
              onClick={() => setConfirmingExit(false)}
            >
              Stay
            </button>
            <button
              type="button"
              className="wb-gms-entry"
              style={{ width: 'auto', marginBottom: 0 }}
              onClick={onEnd}
            >
              Leave
            </button>
          </div>
        </div>
      ) : null}
    </>,
    document.body
  );
}
